import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'
import hash from 'object-hash'
import { pick } from 'ramda'

import { mergeDeepAll } from '../utils'
import {
  CompassAPIDataBase,
  DataSource,
  getDataSources,
  StatusFlag,
} from '../utils/compass'
import type { BaseContext } from './'
import { commonChartOptions } from './common'

const ONE_MINUTE_MS = 60 * 1000

const parseDate = (date: string) => {
  const [year, month, day] = date.split('-').map((value) => parseInt(value))
  return new Date(year, month - 1, day).getTime()
}

export namespace Upside {
  const CONTEXT_ID = 'equity:upside'
  const REQUEST_URL = '/api/upside/submit'
  const CHART_IDS = [
    'tot_common_eqy_mean',
    'roe',
    'net_income_growth',
    'common_dividends',
    'payout',
    'erp',
    'simple_erp',
    'ntnb_10y',
  ] as const
  const KE_CHART_IDS = ['ke_gringo', 'ke_local', 'ke_medio'] as const

  export const __typelist: {
    config: ChartConfig
    data: ChartData
    context: ContextType
  } = {} as any

  export type ContextType = BaseContext<
    typeof CONTEXT_ID,
    ChartConfig,
    ChartData
  >

  export interface ChartConfig {
    chart: typeof CHART_IDS[number] | 'ke'
    context: typeof CONTEXT_ID
    basket_id: string
    basket_username: string
    weights_id: string
    title?: string
  }

  export interface ChartData extends CompassAPIDataBase {
    charts: {
      id: string
      name: string
      unit: string
      decimals: number
      values: {
        date: string
        value: number
      }[]
      data_sources: DataSource[] | null
      status_flag?: StatusFlag
    }[]
  }

  const getChartData = async (
    config: ChartConfig,
    modifier: (config: AxiosRequestConfig) => AxiosRequestConfig = (conf) =>
      conf
  ) => {
    const response = (await axios(
      modifier({
        url: REQUEST_URL,
        method: 'POST',
        timeout: ONE_MINUTE_MS,
        data: {
          basket_id: config.basket_id,
          basket_username: config.basket_username,
          weights_id: config.weights_id,
        },
      })
    )) as AxiosResponse<{ charts: ChartData['charts'] }>

    const charts = response.data.charts.filter((chart) =>
      config.chart === 'ke'
        ? KE_CHART_IDS.some((id) => chart.id === id)
        : chart.id === config.chart
    )
    return {
      charts,
      ...pick(['data_sources', 'status_flag'], charts[0] ?? {}),
    }
  }

  const getChartProps = (config: ChartConfig, data: ChartData) => {
    const series = data.charts.map((chart) => ({
      id: chart.id,
      name: chart.name,
      type: 'line',
      yAxis: 'default',
      data: chart.values.map((point) => [parseDate(point.date), point.value]),
      marker: { enabled: false },
      tooltip: {
        valueDecimals: chart.decimals,
        valueSuffix: chart.unit && ` ${chart.unit}`,
      },
    }))

    const unit = data.charts[0]?.unit ?? null

    const yAxis = {
      id: 'default',
      opposite: false,
      title: {
        align: 'high',
        text: unit ? `(${unit})` : undefined,
        rotation: 0,
        offset: 15,
      },
    }

    const tooltip = {
      shared: true,
      split: false,
      xDateFormat: '%B %e, %Y',
    }

    const creditsConfig = getDataSources(data.data_sources)

    return {
      id: Context.getChartId(config),
      title: config.chart === 'ke' ? 'Ke' : data.charts[0]?.name ?? '',
      options: mergeDeepAll([
        creditsConfig,
        commonChartOptions,
        {
          series,
          yAxis,
          tooltip,
        },
      ]),
      stockTools: true,
    }
  }

  export const Context: ContextType = {
    id: CONTEXT_ID,
    getChartId: (config) => `${config.context}:${config.chart}:${hash(config)}`,
    getChartData,
    getChartProps,
  }
}
